import React, { useState, useCallback } from 'react';
import {
  TextField,
  Autocomplete,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Grid
} from '@mui/material';
import { debounce } from 'lodash';
import axios from 'axios';
import { validatePostcode } from '../../utils/validation';

interface AddressData {
  line1?: string;
  line2?: string;
  line3?: string;
  city?: string;
  postcode?: string;
  county?: string;
  country?: string;
  // Alternative field names for different forms
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  street?: string;
}

interface AutocompleteResult {
  suggestion: string;
  udprn: number;
}

type FieldMapping = {
  line1: string;
  line2: string;
  line3: string;
  city: string;
  postcode: string;
  county: string;
  country: string;
};

interface AddressAutocompleteProps {
  value: AddressData;
  onChange: (address: AddressData) => void;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  fieldMapping?: Partial<FieldMapping>; // Maps API fields to form fields
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  disabled = false,
  required = false,
  label = 'Address',
  fieldMapping = {
    line1: 'line1',
    line2: 'line2',
    line3: 'line3',
    city: 'city',
    postcode: 'postcode',
    county: 'county',
    country: 'country'
  }
}) => {
  const [manualEntry, setManualEntry] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<AutocompleteResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<AutocompleteResult | null>(null);
  const [postcodeError, setPostcodeError] = useState<string>('');
  
  // Ideal Postcodes configuration
  const IDEAL_POSTCODES_API_KEY = process.env.REACT_APP_IDEAL_POSTCODES_API_KEY || '';
  const IDEAL_POSTCODES_BASE_URL = 'https://api.ideal-postcodes.co.uk/v1';

  // Get the actual field name from mapping
  const getFieldName = (apiField: keyof FieldMapping): string => {
    return fieldMapping[apiField] || apiField;
  };

  // Debounced search function
  const searchAddresses = useCallback(
    debounce(async (query: string) => {
      if (query.length < 3) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      try {
        // Call Ideal Postcodes autocomplete API directly
        const response = await axios.get(`${IDEAL_POSTCODES_BASE_URL}/autocomplete/addresses`, {
          params: {
            api_key: IDEAL_POSTCODES_API_KEY,
            query
          }
        });
        const hits = response.data.result?.hits || [];
        setSuggestions(hits.map((hit: any) => ({
          suggestion: hit.suggestion,
          udprn: hit.udprn
        })));
      } catch (error) {
        console.error('Address autocomplete error:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 500),
    [IDEAL_POSTCODES_API_KEY, IDEAL_POSTCODES_BASE_URL]
  );

  // Handle search input change
  const handleSearchChange = (event: React.SyntheticEvent, newValue: string) => {
    setSearchQuery(newValue);
    searchAddresses(newValue);
  };

  // Handle address selection
  const handleAddressSelect = async (
    event: React.SyntheticEvent,
    newValue: AutocompleteResult | null
  ) => {
    if (!newValue) {
      setSelectedSuggestion(null);
      return;
    }

    setSelectedSuggestion(newValue);
    setLoading(true);

    try {
      // Call Ideal Postcodes UDPRN lookup API directly
      const response = await axios.get(`${IDEAL_POSTCODES_BASE_URL}/udprn/${newValue.udprn}`, {
        params: {
          api_key: IDEAL_POSTCODES_API_KEY
        }
      });
      const addressData = response.data.result;
      
      // Map API fields to form fields using fieldMapping
      const mappedAddress: any = {};
      mappedAddress[getFieldName('line1')] = addressData.line_1 || '';
      mappedAddress[getFieldName('line2')] = addressData.line_2 || '';
      mappedAddress[getFieldName('line3')] = addressData.line_3 || '';
      mappedAddress[getFieldName('city')] = addressData.post_town || '';
      mappedAddress[getFieldName('postcode')] = addressData.postcode || '';
      mappedAddress[getFieldName('county')] = addressData.county || '';
      mappedAddress[getFieldName('country')] = addressData.country || 'United Kingdom';
      
      onChange(mappedAddress);
    } catch (error) {
      console.error('Address lookup error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle manual entry toggle
  const handleManualToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setManualEntry(event.target.checked);
    if (!event.target.checked) {
      // Reset to autocomplete mode
      setSearchQuery('');
      setSuggestions([]);
      setSelectedSuggestion(null);
    }
  };

  // Handle manual field changes
  const handleFieldChange = (apiField: keyof FieldMapping) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const fieldName = getFieldName(apiField);
    onChange({
      ...value,
      [fieldName]: event.target.value
    });
  };

  // Get field value using mapping
  const getFieldValue = (apiField: keyof FieldMapping): string => {
    const fieldName = getFieldName(apiField);
    return (value as any)[fieldName] || '';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
          {label} {required && <span style={{ color: '#f44336' }}>*</span>}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={manualEntry}
              onChange={handleManualToggle}
              disabled={disabled}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#6366f1',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#6366f1',
                },
              }}
            />
          }
          label="Enter manually"
          sx={{ '& .MuiFormControlLabel-label': { color: 'rgba(255, 255, 255, 0.7)' } }}
        />
      </Box>

      {!manualEntry ? (
        // Autocomplete mode
        <Box>
          <Autocomplete
            options={suggestions}
            getOptionLabel={(option) => option.suggestion}
            value={selectedSuggestion}
            onChange={handleAddressSelect}
            inputValue={searchQuery}
            onInputChange={handleSearchChange}
            loading={loading}
            disabled={disabled}
            filterOptions={(x) => x} // Disable client-side filtering
            noOptionsText={searchQuery.length < 3 ? 'Type at least 3 characters' : 'No addresses found'}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search for your address"
                placeholder="Start typing your address or postcode..."
                required={required}
                className="theme-input"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-input': { color: 'white' },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                  '& .MuiAutocomplete-popupIndicator': { color: 'white' },
                  '& .MuiAutocomplete-clearIndicator': { color: 'white' },
                }}
              />
            )}
            sx={{
              mb: 2,
              '& .MuiAutocomplete-popper': {
                '& .MuiPaper-root': {
                  bgcolor: '#1e3a8a',
                  color: 'white',
                },
                '& .MuiAutocomplete-option': {
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                  '&.Mui-focused': { bgcolor: 'rgba(99, 102, 241, 0.3)' },
                },
                '& .MuiAutocomplete-noOptions': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
              },
            }}
            componentsProps={{
              popper: {
                sx: {
                  '& .MuiPaper-root': {
                    bgcolor: '#1e3a8a',
                    color: 'white',
                  },
                  '& .MuiAutocomplete-option': {
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
                    '&.Mui-focused': { bgcolor: 'rgba(99, 102, 241, 0.3)' },
                  },
                  '& .MuiAutocomplete-noOptions': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                },
              },
            }}
          />

          {/* Display selected address (read-only) */}
          {getFieldValue('line1') && (
            <Box
              sx={{
                p: 2,
                bgcolor: 'rgba(33, 150, 243, 0.1)',
                borderRadius: 1,
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'rgba(255, 255, 255, 0.95)' }}>
                Selected Address:
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>{getFieldValue('line1')}</Typography>
              {getFieldValue('line2') && <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>{getFieldValue('line2')}</Typography>}
              {getFieldValue('line3') && <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>{getFieldValue('line3')}</Typography>}
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>{getFieldValue('city')}</Typography>
              {getFieldValue('county') && <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>{getFieldValue('county')}</Typography>}
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>{getFieldValue('postcode')}</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>{getFieldValue('country')}</Typography>
            </Box>
          )}
        </Box>
      ) : (
        // Manual entry mode
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 1"
              value={getFieldValue('line1')}
              onChange={handleFieldChange('line1')}
              disabled={disabled}
              required={required}
              className="theme-input"
              sx={{
                '& .MuiInputBase-input': { color: 'white' },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 2"
              value={getFieldValue('line2')}
              onChange={handleFieldChange('line2')}
              disabled={disabled}
              className="theme-input"
              sx={{
                '& .MuiInputBase-input': { color: 'white' },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 3"
              value={getFieldValue('line3')}
              onChange={handleFieldChange('line3')}
              disabled={disabled}
              className="theme-input"
              sx={{
                '& .MuiInputBase-input': { color: 'white' },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City/Town"
              value={getFieldValue('city')}
              onChange={handleFieldChange('city')}
              disabled={disabled}
              required={required}
              className="theme-input"
              sx={{
                '& .MuiInputBase-input': { color: 'white' },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="County"
              value={getFieldValue('county')}
              onChange={handleFieldChange('county')}
              disabled={disabled}
              className="theme-input"
              sx={{
                '& .MuiInputBase-input': { color: 'white' },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Postcode"
              value={getFieldValue('postcode')}
              onChange={(e) => {
                const uppercased = e.target.value.toUpperCase();
                const fieldName = getFieldName('postcode');
                onChange({
                  ...value,
                  [fieldName]: uppercased
                });
                // Validate postcode format
                if (uppercased) {
                  const result = validatePostcode(uppercased);
                  setPostcodeError(result.error || '');
                } else {
                  setPostcodeError('');
                }
              }}
              onBlur={() => {
                const postcodeValue = getFieldValue('postcode');
                if (postcodeValue) {
                  const result = validatePostcode(postcodeValue);
                  setPostcodeError(result.error || '');
                }
              }}
              disabled={disabled}
              required={required}
              error={!!postcodeError}
              helperText={postcodeError || 'UK format: SW1A 1AA'}
              className="theme-input"
              inputProps={{
                style: { textTransform: 'uppercase' }
              }}
              sx={{
                '& .MuiInputBase-input': { color: 'white' },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                '& .MuiFormHelperText-root': { 
                  color: postcodeError ? '#f44336' : 'rgba(255, 255, 255, 0.7)'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Country"
              value={getFieldValue('country')}
              onChange={handleFieldChange('country')}
              disabled={disabled}
              required={required}
              className="theme-input"
              sx={{
                '& .MuiInputBase-input': { color: 'white' },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
              }}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AddressAutocomplete;
